/**
 * Route-corridor filtering for landscape furniture (perf fix for the walls /
 * water freeze cycle). Scenery like drystone walls is only visually
 * discernible near the viewer's likely positions (the fly-through route), so
 * layers clip their source polylines to a corridor around the route before
 * doing any ground sampling — most of the sampling work then never exists.
 *
 * Pure geometry, three.js-free, unit-tested in Node. The corridor test is
 * deliberately approximate (the corridor polyline is downsampled for speed):
 * it decides which scenery is worth building, never where anything sits.
 */
import type { LocalXZ } from '../core/coordinates';

/** Corridor points closer together than this are skipped when measuring —
 * bounds the cost of clipping thousands of vertices against a long GPX track. */
const CORRIDOR_SPACING_M = 75;

function distToSegmentSq(p: LocalXZ, a: LocalXZ, b: LocalXZ): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const lenSq = abx * abx + abz * abz;
  let t = 0;
  if (lenSq > 0) t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.z - a.z) * abz) / lenSq));
  const dx = p.x - (a.x + abx * t);
  const dz = p.z - (a.z + abz * t);
  return dx * dx + dz * dz;
}

/** Exact minimum distance (m) from a point to a polyline. */
export function distanceToPolylineM(p: LocalXZ, line: LocalXZ[]): number {
  if (line.length === 0) return Infinity;
  if (line.length === 1) return Math.hypot(p.x - line[0].x, p.z - line[0].z);
  let best = Infinity;
  for (let i = 1; i < line.length; i++) {
    best = Math.min(best, distToSegmentSq(p, line[i - 1], line[i]));
  }
  return Math.sqrt(best);
}

/** Drop corridor points closer than spacingM to the previously kept point
 * (keeps endpoints). Shape error is bounded by the spacing, which is well
 * inside any sensible corridor width. */
function downsample(line: LocalXZ[], spacingM: number): LocalXZ[] {
  if (line.length <= 2) return line;
  const out: LocalXZ[] = [line[0]];
  for (let i = 1; i < line.length - 1; i++) {
    const prev = out[out.length - 1];
    if (Math.hypot(line[i].x - prev.x, line[i].z - prev.z) >= spacingM) out.push(line[i]);
  }
  out.push(line[line.length - 1]);
  return out;
}

/**
 * Splits `line` into the maximal runs of consecutive vertices that lie within
 * maxDistM of the corridor polyline. Runs shorter than 2 points are dropped
 * (nothing can be extruded from a single point).
 */
export function clipToCorridor(line: LocalXZ[], corridor: LocalXZ[], maxDistM: number): LocalXZ[][] {
  const coarse = downsample(corridor, CORRIDOR_SPACING_M);
  const maxSq = maxDistM * maxDistM;
  const runs: LocalXZ[][] = [];
  let run: LocalXZ[] = [];
  for (const p of line) {
    let within = false;
    if (coarse.length === 1) {
      const dx = p.x - coarse[0].x;
      const dz = p.z - coarse[0].z;
      within = dx * dx + dz * dz <= maxSq;
    } else {
      for (let i = 1; i < coarse.length; i++) {
        if (distToSegmentSq(p, coarse[i - 1], coarse[i]) <= maxSq) {
          within = true;
          break;
        }
      }
    }
    if (within) {
      run.push(p);
    } else if (run.length > 0) {
      if (run.length >= 2) runs.push(run);
      run = [];
    }
  }
  if (run.length >= 2) runs.push(run);
  return runs;
}
